// Espelhado em apps/admin/src/theme/brandContrast.ts (mesma lógica, só muda a formatação): as duas cópias devem mudar juntas.
// Contraste por marca: texto na cor da marca usa `brandText` (legível sobre fundo claro) e texto
// branco sobre fundo da marca usa `onBrand`. Nas marcas sem esses campos, `brandText` é a própria
// `brand` e `onBrand` é #FFFFFF, então nada muda nelas.

type StyleObject = Record<string, any>;
type Palette = Record<string, string>;

const BRAND_VALUE = /^(colors\.)?brand\.(\d+)$/;
const TEXT_PROPS = ['color', 'textColor'];
const BG_PROPS = ['bg', 'bgColor', 'background', 'backgroundColor'];
const WHITE_VALUES = ['white', 'colors.white'];
// Variáveis CSS do Chakra 2.10 que terminam em "-color" mas pintam borda, sombra ou preenchimento:
// --tabs-border-color, --avatar-border-color, --card-border-color, --number-input-border-color,
// --popper-arrow-shadow-color, --stepper-accent-color, --skeleton-start-color, --skeleton-end-color.
const NON_TEXT_COLOR_VAR = /-(border|shadow|accent|start|end)-color$/;

const isStyleObject = (value: unknown): value is StyleObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isCssVar = (key: string) => key.startsWith('--');

const isTextKey = (key: string) =>
  TEXT_PROPS.includes(key) || (isCssVar(key) && /-(color|fg)$/.test(key) && !NON_TEXT_COLOR_VAR.test(key));

const isBgKey = (key: string) => BG_PROPS.includes(key) || (isCssVar(key) && key.endsWith('-bg'));

// Mapa "hex da marca" -> "hex de brandText", para estilos em que o Chakra já resolveu a cor com
// getColor(theme, `${c}.N`) (ex.: Tabs soft-rounded).
type BrandHexMap = Map<string, string>;

const isBrandValue = (value: unknown, hexMap?: BrandHexMap) =>
  typeof value === 'string' && (BRAND_VALUE.test(value) || !!hexMap?.has(value.toLowerCase()));

const toTextValue = (value: string, onBrandBg: boolean, hexMap?: BrandHexMap) => {
  if (BRAND_VALUE.test(value)) return value.replace('brand.', 'brandText.');
  const hex = hexMap?.get(value.toLowerCase());
  if (hex) return hex;
  if (onBrandBg && WHITE_VALUES.includes(value)) return value.replace('white', 'onBrand');
  return value;
};

export function applyBrandContrast<T>(style: T, hexMap?: BrandHexMap): T {
  if (!isStyleObject(style)) return style;
  const onBrandBg = Object.keys(style).some((key) => isBgKey(key) && isBrandValue(style[key], hexMap));
  const result: StyleObject = {};
  Object.keys(style).forEach((key) => {
    const value = style[key];
    if (isStyleObject(value)) result[key] = applyBrandContrast(value, hexMap);
    else if (isTextKey(key) && typeof value === 'string') result[key] = toTextValue(value, onBrandBg, hexMap);
    else result[key] = value;
  });
  return result as T;
}

const hexMapCache = new WeakMap<object, BrandHexMap>();

const brandHexMap = (theme: any): BrandHexMap | undefined => {
  const brand: Palette | undefined = theme?.colors?.brand;
  const brandText: Palette | undefined = theme?.colors?.brandText;
  if (!isStyleObject(brand) || !isStyleObject(brandText)) return undefined;
  const cached = hexMapCache.get(theme);
  if (cached) return cached;
  const map: BrandHexMap = new Map();
  Object.keys(brand).forEach((shade) => {
    if (typeof brand[shade] === 'string' && typeof brandText[shade] === 'string') {
      map.set(brand[shade].toLowerCase(), brandText[shade]);
    }
  });
  hexMapCache.set(theme, map);
  return map;
};

const wrapStyle = (style: unknown) => {
  if (typeof style === 'function') {
    return (props: any) => applyBrandContrast(style(props), brandHexMap(props?.theme));
  }
  return applyBrandContrast(style);
};

// `components` é opcional no tipo porque o extendTheme do Chakra devolve Record<string, any>.
export function withBrandContrast<T extends { components?: Record<string, any> }>(theme: T): T {
  const source = theme.components;
  if (!isStyleObject(source)) return theme;
  const components: Record<string, any> = {};
  Object.keys(source).forEach((name) => {
    const config = source[name];
    if (!isStyleObject(config)) {
      components[name] = config;
      return;
    }
    const wrapped: StyleObject = { ...config };
    if (config.baseStyle !== undefined) wrapped.baseStyle = wrapStyle(config.baseStyle);
    if (isStyleObject(config.variants)) {
      wrapped.variants = {};
      Object.keys(config.variants).forEach((variant) => {
        wrapped.variants[variant] = wrapStyle(config.variants[variant]);
      });
    }
    components[name] = wrapped;
  });
  return { ...theme, components };
}
